/**
 * Mobile-only looping carousel for shared product showcases.
 * Below 750px the product grid becomes a one-per-view horizontal carousel that
 * loops seamlessly, auto-advances, syncs a dot indicator, and supports touch
 * swiping. On larger viewports it stays a static CSS grid and the carousel
 * logic is inert (no transform, no autoplay).
 *
 * The loop works by reordering the DOM: after each slide the outgoing card is
 * moved to the other end of the track and the transform resets to 0, so the
 * first child is always the visible card. Each card gets a data-fp-index at
 * init so the dots can track the logical position across reorders.
 */
if (!customElements.get('featured-products-carousel')) {
  class FeaturedProductsCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-carousel-track]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 4500;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.dragging = false;
    this.dragEngaged = false;

    this.next = this.next.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
  }

  connectedCallback() {
    if (!this.track || this.track.children.length < 2) return;

    Array.from(this.track.children).forEach((item, i) => {
      item.dataset.fpIndex = String(i);
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.restartAutoplay();
      });
    });

    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd);
    this.addEventListener('touchcancel', this.onTouchEnd);

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stopAutoplay() : this.startAutoplay();
    });

    this.mobile.addEventListener('change', this.handleViewportChange);
    this.handleViewportChange();
    this.updateDots();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.mobile.removeEventListener('change', this.handleViewportChange);
  }

  handleViewportChange() {
    if (this.mobile.matches) {
      this.startAutoplay();
    } else {
      // Leave the desktop grid untouched — clear any inline transform/transition.
      this.stopAutoplay();
      this.track.style.transition = 'none';
      this.track.style.transform = 'translateX(0)';
    }
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return 0;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  get currentIndex() {
    return parseInt(this.track.firstElementChild.dataset.fpIndex, 10) || 0;
  }

  updateDots() {
    const current = this.currentIndex;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === current);
      if (i === current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  next() {
    if (this.isAnimating || this.dragging || !this.mobile.matches) return;
    this.isAnimating = true;
    const distance = this.step;

    if (this.reduceMotion) {
      this.track.appendChild(this.track.firstElementChild);
      this.updateDots();
      this.isAnimating = false;
      return;
    }

    this.track.style.transition = 'transform 0.5s ease';
    this.track.style.transform = `translateX(-${distance}px)`;
    this.track.addEventListener(
      'transitionend',
      () => {
        this.track.style.transition = 'none';
        this.track.appendChild(this.track.firstElementChild);
        this.track.style.transform = 'translateX(0)';
        // Force reflow so the next transition starts from the reset position.
        void this.track.offsetHeight;
        this.updateDots();
        this.isAnimating = false;
      },
      { once: true }
    );
  }

  goTo(index) {
    if (this.isAnimating || this.dragging || !this.mobile.matches) return;
    const count = this.track.children.length;
    const stepsForward = (index - this.currentIndex + count) % count;
    if (stepsForward === 0) return;

    this.track.style.transition = 'none';
    for (let i = 0; i < stepsForward; i++) {
      this.track.appendChild(this.track.firstElementChild);
    }
    this.track.style.transform = 'translateX(0)';
    this.updateDots();
  }

  /* ---------------- Touch swiping ---------------- */

  onTouchStart(event) {
    if (this.isAnimating || !this.mobile.matches) return;
    this.dragging = true;
    this.dragEngaged = false;
    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.dragDx = 0;
    this.stopAutoplay();
  }

  onTouchMove(event) {
    if (!this.dragging) return;
    const dx = event.touches[0].clientX - this.startX;
    const dy = event.touches[0].clientY - this.startY;

    if (!this.dragEngaged) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        // Vertical scroll — hand the gesture back to the page.
        this.dragging = false;
        this.startAutoplay();
        return;
      }
      this.dragEngaged = true;
      this.dragStep = this.step;
      this.track.style.transition = 'none';
      // Prepend the last card so a rightward drag reveals the previous product
      // instead of a blank gap; base position shifts to -step to compensate.
      this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
    }

    this.dragDx = Math.max(-this.dragStep, Math.min(this.dragStep, dx));
    this.track.style.transform = `translateX(${this.dragDx - this.dragStep}px)`;
  }

  onTouchEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    if (!this.dragEngaged) {
      this.startAutoplay();
      return;
    }
    this.dragEngaged = false;

    const step = this.dragStep;
    const threshold = Math.min(step * 0.2, 60);
    this.isAnimating = true;

    const finalize = (removeFromFront) => {
      this.track.style.transition = 'none';
      for (let i = 0; i < removeFromFront; i++) {
        this.track.appendChild(this.track.firstElementChild);
      }
      this.track.style.transform = 'translateX(0)';
      void this.track.offsetHeight;
      this.updateDots();
      this.isAnimating = false;
      this.startAutoplay();
    };

    const settleTo = (targetX, removeFromFront) => {
      if (this.reduceMotion || targetX === this.dragDx - step) {
        finalize(removeFromFront);
        return;
      }
      this.track.style.transition = 'transform 0.3s ease';
      this.track.style.transform = `translateX(${targetX}px)`;
      this.track.addEventListener('transitionend', () => finalize(removeFromFront), { once: true });
    };

    if (this.dragDx > threshold) {
      // Swipe right — the prepended previous card becomes current.
      settleTo(0, 0);
    } else if (this.dragDx < -threshold) {
      // Swipe left — advance past the prepended card and the old current one.
      settleTo(-2 * step, 2);
    } else {
      // Below threshold — snap back and undo the prepend.
      settleTo(-step, 1);
    }
  }

  startAutoplay() {
    if (this.reduceMotion || this.autoplayTimer || this.autoplayDelay <= 0) return;
    if (!this.mobile.matches) return;
    this.autoplayTimer = window.setInterval(this.next, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
  }

  customElements.define('featured-products-carousel', FeaturedProductsCarousel);
}
